import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import useChatMessageStore from '../../stores/useChatMessageStore';
import type { ChatUIAffectingMessage } from '../../types/centrifuge/chatTypes';
import limitChatMessagesLength from './limitChatMessagesLength';
const OVERLOAD_THRESHOLD = 7; // 과부하 판단 기준: 1초 내 이 개수 이상의 메시지가 오면 배치 모드 활성화

interface ChatBatcherProps {
  batchInterval?: number; // 배치 모드에서 실제 DOM 업데이트 간격 (ms)
}
interface UseChatBatcherReturn {
  processChatMessage: (chatItem: ChatUIAffectingMessage) => void;
  isBatchModeActive: boolean;
}
let memoizedThresholdQueue: number[] | null = null;
const createInitialQueue = (): number[] => {
  if (memoizedThresholdQueue) {
    return memoizedThresholdQueue;
  }
  memoizedThresholdQueue = new Array(OVERLOAD_THRESHOLD).fill(0);
  return memoizedThresholdQueue;
};

/**
 * 과부하 상태 판단: 최근 threshold개 메시지가 1초 안에 들어왔는지 확인
 * 순환큐의 첫 번째와 마지막 타임스탬프 차이로 계산
 */
const checkOverloadStatus = (timestampQueue: number[], queueIndex: number, samplesCount: number, isBatchModeActive: boolean): boolean => {
  if (samplesCount < OVERLOAD_THRESHOLD) {
    return false; // 아직 충분한 샘플이 없음
  }
  const oldestTime = timestampQueue[queueIndex];
  const newestTime = timestampQueue[(queueIndex - 1 + OVERLOAD_THRESHOLD) % OVERLOAD_THRESHOLD];
  const timeDiff = newestTime - oldestTime;
  if (isBatchModeActive) {
    // 배치 모드 해제 기준: 1.2초
    return timeDiff < 1200;
  } else {
    // 배치 모드 활성화 기준: 1초
    return timeDiff < 1000;
  }
};
const useChatBatcher = t0 => {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    batchInterval: t2
  } = t1;
  const batchInterval = t2 === undefined ? 180 : t2;
  const setChatMessages = useChatMessageStore(_temp);
  const [isBatchModeActive, setIsBatchModeActive] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const chatBuffer = useRef(t3);
  const batchTimer = useRef(null);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = createInitialQueue();
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const timestampQueue = useRef(t4);
  const queueIndex = useRef(0);
  const samplesCount = useRef(0);
  let t5;
  if ($[4] !== setChatMessages) {
    t5 = () => {
      if (chatBuffer.current.length === 0) {
        return;
      }
      const currentChatMessages = useChatMessageStore.getState().chatMessages;
      const updatedChatMessages = limitChatMessagesLength(currentChatMessages, chatBuffer.current);
      setChatMessages(updatedChatMessages);
      chatBuffer.current = [];
    };
    $[4] = setChatMessages;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const flushBuffer = t5;
  let t6;
  if ($[6] !== batchInterval || $[7] !== flushBuffer) {
    t6 = () => {
      if (batchTimer.current) {
        return;
      }
      batchTimer.current = setTimeout(() => {
        flushBuffer();
        batchTimer.current = null;
      }, batchInterval);
    };
    $[6] = batchInterval;
    $[7] = flushBuffer;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const delayedFlush = t6;
  let t7;
  if ($[9] !== delayedFlush || $[10] !== flushBuffer || $[11] !== isBatchModeActive || $[12] !== setChatMessages) {
    t7 = chatItem => {
      const now = Date.now();
      timestampQueue.current[queueIndex.current] = now;
      queueIndex.current = (queueIndex.current + 1) % OVERLOAD_THRESHOLD;
      samplesCount.current = Math.min(samplesCount.current + 1, OVERLOAD_THRESHOLD);
      const isOverloaded = checkOverloadStatus(timestampQueue.current, queueIndex.current, samplesCount.current, isBatchModeActive);
      (chatItem as {
        receivedInBatchMode?: boolean;
      }).receivedInBatchMode = isOverloaded;
      if (isOverloaded && !isBatchModeActive) {
        setIsBatchModeActive(true);
      } else {
        if (!isOverloaded && isBatchModeActive) {
          setIsBatchModeActive(false);
          chatBuffer.current.push(chatItem);
          flushBuffer();
          return;
        }
      }
      if (isBatchModeActive) {
        chatBuffer.current.push(chatItem);
        delayedFlush();
      } else {
        const currentChatMessages_0 = useChatMessageStore.getState().chatMessages;
        const updatedChatMessages_0 = limitChatMessagesLength(currentChatMessages_0, chatItem);
        setChatMessages(updatedChatMessages_0);
      }
    };
    $[9] = delayedFlush;
    $[10] = flushBuffer;
    $[11] = isBatchModeActive;
    $[12] = setChatMessages;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const processChatMessage = t7;
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      if (batchTimer.current) {
        clearTimeout(batchTimer.current);
      }
    };
    t9 = [];
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== isBatchModeActive || $[17] !== processChatMessage) {
    t10 = {
      processChatMessage,
      isBatchModeActive
    };
    $[16] = isBatchModeActive;
    $[17] = processChatMessage;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
export default useChatBatcher;
function _temp(state) {
  return state.setChatMessages;
}